/**
 * ChecklistBankPage — shared Master UI for Tech Panel + Offer question banks.
 *
 * Both banks have an identical base shape (text + model_answer + red_flags +
 * topic + status, panel additionally has difficulty). Both require
 * Industry → Department → Role taxonomy. Panel may opt into Work Assessment
 * category scoping via `withDepartmentCategory`. Both may enable CSV bulk
 * upload via `showBulkUpload` / `bulkKind`.
 *
 * Sprint 11.6 (#10) — MVP scope: pure checklist reference data. No grading
 * rubric, no per-question scoring, no images. Interviewer reads + writes
 * free-form notes in the existing panel_artifacts/panel_evaluations tables.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Post, v1Patch } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Save, ListChecks, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import QuestionBankBulkUpload from '@/components/QuestionBankBulkUpload';
import QuestionBankReadOnlyBanner from '@/components/QuestionBankReadOnlyBanner';
import UnsavedRefreshGuard from '@/components/UnsavedRefreshGuard';
import { Gated } from '@/auth/permissions';
import { bankWriteErrorMessage, useBankEditGuard } from '@/hooks/useBankEditGuard';
import {
  QuestionBankBulkDeleteBar,
  QuestionEditorDeleteButton,
  QuestionRowCheckbox,
  useCheckedQuestionIds,
} from '@/components/QuestionBankDeleteControls';
import IndustryDeptRoleCascade, {
  EMPTY_TAXONOMY,
} from './IndustryDeptRoleCascade';

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const STATUSES = ['draft', 'active', 'retired'];

const BLANK_FORM = {
  text: '',
  model_answer: '',
  red_flags: '',
  topic: '',
  difficulty: 'medium',
  status: 'draft',
  industryId: '',
  departmentId: '',
  roleId: '',
};

function isNewFormDirty(form) {
  return form.text.trim() !== ''
    || form.model_answer.trim() !== ''
    || form.red_flags.trim() !== ''
    || form.topic !== ''
    || form.departmentId !== ''
    || form.industryId !== ''
    || form.roleId !== ''
    || form.difficulty !== BLANK_FORM.difficulty
    || form.status !== BLANK_FORM.status;
}

export function ChecklistBankPage({
  pageTitle,
  pageSubtitle,
  apiPath,
  queryKey,
  withDifficulty,
  testIdPrefix,
  editPermission,
  withDepartmentCategory = false,
  showBulkUpload = false,
  bulkKind = 'panel',
}) {
  const qc = useQueryClient();
  const { readOnly, denyEdit, guard } = useBankEditGuard(editPermission);
  const [filterTax, setFilterTax] = useState({ ...EMPTY_TAXONOMY });
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data: departments = [] } = useQuery({
    queryKey: ['v1-master-departments'],
    queryFn: () => v1Get('/master/departments'),
  });

  const listQueryKey = [
    ...(Array.isArray(queryKey) ? queryKey : [queryKey]),
    filterTax.industryId,
    filterTax.departmentId,
    filterTax.roleId,
  ];

  const { data: questions = [], isLoading } = useQuery({
    queryKey: listQueryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterTax.industryId) params.set('industry_id', filterTax.industryId);
      if (filterTax.departmentId) params.set('department_id', filterTax.departmentId);
      if (filterTax.roleId) params.set('role_id', filterTax.roleId);
      const qs = params.toString();
      return v1Get(qs ? `${apiPath}?${qs}` : apiPath);
    },
  });
  const [selectedId, setSelectedId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const discardConfirmedRef = useRef(false);
  const { checked, toggle, clear, setAll } = useCheckedQuestionIds();
  const [form, setForm] = useState({ ...BLANK_FORM });

  const formTax = useMemo(
    () => ({
      industryId: form.industryId,
      departmentId: form.departmentId,
      roleId: form.roleId,
    }),
    [form.industryId, form.departmentId, form.roleId],
  );

  const onFormTaxChange = useCallback((next) => {
    setForm((prev) => ({
      ...prev,
      industryId: next.industryId,
      departmentId: next.departmentId,
      roleId: next.roleId,
      topic: withDepartmentCategory && next.departmentId !== prev.departmentId
        ? ''
        : prev.topic,
    }));
  }, [withDepartmentCategory]);

  const { data: createCategories = [] } = useQuery({
    queryKey: ['v1-work-categories', form.departmentId],
    queryFn: () => v1Get(`/questions/work-categories?department_id=${form.departmentId}`),
    enabled: withDepartmentCategory && !!form.departmentId,
  });

  const selected = questions.find((q) => q.id === selectedId) || questions[0] || null;

  const resetForm = () => setForm({ ...BLANK_FORM });

  const handleNewDialogOpenChange = (open) => {
    if (!open) {
      if (isNewFormDirty(form)) {
        setNewOpen(false);
        setDiscardConfirmOpen(true);
        return;
      }
      resetForm();
    }
    setNewOpen(open);
  };

  const handleDiscardConfirmOpenChange = (open) => {
    setDiscardConfirmOpen(open);
    if (!open) {
      if (discardConfirmedRef.current) {
        discardConfirmedRef.current = false;
        return;
      }
      if (isNewFormDirty(form)) {
        setNewOpen(true);
      }
    }
  };

  const confirmDiscardNewForm = () => {
    discardConfirmedRef.current = true;
    resetForm();
    setNewOpen(false);
  };

  const afterDelete = (ids) => {
    const gone = new Set(Array.isArray(ids) ? ids : [ids]);
    clear();
    qc.invalidateQueries({ queryKey: Array.isArray(queryKey) ? queryKey : [queryKey] });
    if (selected && gone.has(selected.id)) setSelectedId(null);
  };

  const create = async () => {
    if (readOnly) return denyEdit();
    if (!form.roleId) return toast.error('Industry → Department → Role is required');
    if (!form.departmentId) return toast.error('Department is required');
    if (!form.text.trim() || form.text.trim().length < 3) {
      return toast.error('Question text is required (min 3 chars).');
    }
    if (withDepartmentCategory && !form.topic) {
      return toast.error('Work category is required');
    }
    const status = form.status === 'active' && !form.roleId ? 'draft' : form.status;
    try {
      const body = {
        text: form.text.trim(),
        model_answer: form.model_answer.trim() || null,
        red_flags: form.red_flags.trim() || null,
        topic: withDepartmentCategory ? form.topic : (form.topic.trim() || null),
        status,
        role_id: form.roleId,
        department_id: form.departmentId,
      };
      if (withDifficulty) body.difficulty = form.difficulty;
      const created = await v1Post(apiPath, body);
      qc.invalidateQueries({ queryKey: Array.isArray(queryKey) ? queryKey : [queryKey] });
      setSelectedId(created.id);
      setNewOpen(false);
      resetForm();
      toast.success('Question created');
    } catch (e) {
      toast.error(bankWriteErrorMessage(e, 'Could not create question'));
    }
  };

  const patch = async (changes) => {
    if (!selected) return;
    // Text fields auto-save on blur, so this is the last stop before a write the
    // server would reject.
    if (readOnly) return denyEdit();
    try {
      await v1Patch(`${apiPath}/${selected.id}`, changes);
      qc.invalidateQueries({ queryKey: Array.isArray(queryKey) ? queryKey : [queryKey] });
    } catch (e) {
      toast.error(bankWriteErrorMessage(e, 'Could not save'));
    }
  };

  return (
    <div className="space-y-6" data-testid={`${testIdPrefix}-page`}>
      <PageHeader
        title={pageTitle}
        description={pageSubtitle}
        actions={
          <Gated need={editPermission}>
            <div className="flex flex-wrap items-center gap-2">
              {showBulkUpload && (
                <Button
                  variant="outline"
                  onClick={() => setBulkOpen(true)}
                  className="gap-1.5"
                  data-testid={`${testIdPrefix}-bulk`}
                >
                  <FileUp className="h-4 w-4" /> Bulk upload
                </Button>
              )}
              <Button onClick={() => setNewOpen(true)} className="gap-1.5" data-testid={`${testIdPrefix}-new`}>
                <Plus className="h-4 w-4" /> New question
              </Button>
            </div>
          </Gated>
        }
      />

      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-[220px] max-w-sm flex-1">
          <IndustryDeptRoleCascade
            value={filterTax}
            onChange={setFilterTax}
            required={false}
            testIdPrefix={`${testIdPrefix}-filter`}
          />
        </div>
        {(filterTax.industryId || filterTax.departmentId || filterTax.roleId) && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-6"
            onClick={() => setFilterTax({ ...EMPTY_TAXONOMY })}
            data-testid={`${testIdPrefix}-filter-reset`}
          >
            Reset
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <Card className="card-elevated flex max-h-[100vh] flex-col overflow-hidden lg:col-span-5">
          <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-3">
            <div className="mb-2 flex shrink-0 items-center gap-2 px-1">
              <ListChecks className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {questions.length} question{questions.length === 1 ? '' : 's'}
              </p>
            </div>
            <div className="shrink-0">
              <QuestionBankBulkDeleteBar
                ids={questions.map((q) => q.id)}
                checked={checked}
                toggle={toggle}
                setAll={setAll}
                clear={clear}
                apiPath={apiPath}
                editPermission={editPermission}
                onDeleted={afterDelete}
                testIdPrefix={testIdPrefix}
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {isLoading && <p className="px-2 py-4 text-sm text-muted-foreground">Loading…</p>}
              {!isLoading && questions.length === 0 && (
                <p className="px-2 py-6 text-sm text-muted-foreground">
                  No questions yet. Click <span className="font-semibold">New question</span> to add the first one.
                </p>
              )}
              <ul className="space-y-1" data-testid={`${testIdPrefix}-list`}>
                {questions.map((q) => {
                  const isSel = selected?.id === q.id;
                  return (
                    <li key={q.id} className="flex items-start gap-2">
                      <QuestionRowCheckbox
                        id={q.id}
                        checked={checked.has(q.id)}
                        onToggle={toggle}
                        editPermission={editPermission}
                        testId={`${testIdPrefix}-check-${q.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => setSelectedId(q.id)}
                        className={`min-w-0 flex-1 rounded-lg border p-3 text-left text-sm transition-colors ${isSel ? 'border-primary bg-primary/5' : 'hover:bg-accent'}`}
                        data-testid={`${testIdPrefix}-item-${q.id}`}
                      >
                        <p className="line-clamp-2 font-medium">{q.text}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]">
                          <span className={`rounded-full border px-1.5 py-0.5 font-semibold uppercase ${q.status === 'active' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : q.status === 'retired' ? 'border-slate-200 bg-slate-50 text-slate-600' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>{q.status}</span>
                          {withDifficulty && (
                            <span className="rounded-full border bg-secondary px-1.5 py-0.5 uppercase text-muted-foreground">{q.difficulty}</span>
                          )}
                          {q.department_name && (
                            <span className="rounded-full border bg-card px-1.5 py-0.5 text-muted-foreground">{q.department_name}</span>
                          )}
                          {q.topic && <span className="rounded-full border bg-card px-1.5 py-0.5 text-muted-foreground">{q.topic}</span>}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated flex max-h-[100vh] flex-col overflow-hidden lg:col-span-7">
          <CardContent className="min-h-0 flex-1 overflow-y-auto p-5">
            {!selected ? (
              <p className="text-sm text-muted-foreground">
                Select a question from the left to view or edit it.
              </p>
            ) : (
              <ChecklistEditor
                key={selected.id}
                selected={selected}
                withDifficulty={withDifficulty}
                withDepartmentCategory={withDepartmentCategory}
                testIdPrefix={testIdPrefix}
                apiPath={apiPath}
                editPermission={editPermission}
                readOnly={readOnly}
                guard={guard}
                onPatch={patch}
                onDeleted={afterDelete}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={newOpen} onOpenChange={handleNewDialogOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid={`${testIdPrefix}-new-dialog`}>
          <DialogHeader>
            <DialogTitle>New question</DialogTitle>
            <DialogDescription>
              Pick Industry → Department → Role first. You can refine the model answer and red flags later — they auto-save on blur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <IndustryDeptRoleCascade
              value={formTax}
              onChange={onFormTaxChange}
              required
              testIdPrefix={`${testIdPrefix}-new`}
            />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Question text *</p>
              <Textarea
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                rows={3}
                className="mt-1 text-sm"
                data-testid={`${testIdPrefix}-new-text`}
              />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Model answer</p>
              <Textarea
                value={form.model_answer}
                onChange={(e) => setForm({ ...form, model_answer: e.target.value })}
                rows={3}
                className="mt-1 text-sm"
              />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">Red flags</p>
              <Textarea
                value={form.red_flags}
                onChange={(e) => setForm({ ...form, red_flags: e.target.value })}
                rows={2}
                className="mt-1 text-sm border-rose-200"
              />
            </div>
            {withDepartmentCategory ? (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Category *</p>
                <Select
                  value={form.topic}
                  onValueChange={(v) => setForm({ ...form, topic: v })}
                  disabled={!form.departmentId}
                >
                  <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-new-topic`}>
                    <SelectValue placeholder={form.departmentId ? 'Select category' : 'Pick department first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {createCategories.map((c) => (
                      <SelectItem key={c.id || c.label} value={c.label}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.departmentId && createCategories.length === 0 && (
                  <p className="mt-1 text-[11px] text-amber-800">
                    No categories for this department.{' '}
                    <Link to="/portal/settings/roles" className="font-medium underline underline-offset-2">
                      Add categories in Roles &amp; Departments
                    </Link>
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Topic</p>
                <Input
                  value={form.topic}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  className="mt-1 text-sm"
                  placeholder="e.g. Architecture"
                />
              </div>
            )}
            <div className={`grid grid-cols-1 gap-3 ${withDifficulty ? 'md:grid-cols-2' : ''}`}>
              {withDifficulty && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Difficulty</p>
                  <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DIFFICULTIES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</p>
                <Select
                  value={form.roleId ? form.status : (form.status === 'active' ? 'draft' : form.status)}
                  onValueChange={(v) => {
                    if (v === 'active' && !form.roleId) {
                      toast.error('Role is required to activate — keeping draft');
                      setForm({ ...form, status: 'draft' });
                      return;
                    }
                    setForm({ ...form, status: v });
                  }}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => handleNewDialogOpenChange(false)}>Cancel</Button>
            <Button onClick={create} data-testid={`${testIdPrefix}-new-create`}>Create question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={discardConfirmOpen} onOpenChange={handleDiscardConfirmOpenChange}>
        <AlertDialogContent data-testid={`${testIdPrefix}-new-discard-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes. Are you sure you want to discard them?</AlertDialogTitle>
            <AlertDialogDescription>
              Your entered question data will be lost if you discard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testIdPrefix}-new-continue-editing`}>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardNewForm} data-testid={`${testIdPrefix}-new-discard-confirm`}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnsavedRefreshGuard
        armed={newOpen && isNewFormDirty(form)}
        testIdPrefix={`${testIdPrefix}-new`}
      />

      {showBulkUpload && (
        <QuestionBankBulkUpload
          kind={bulkKind}
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          onImported={() => {
            qc.invalidateQueries({ queryKey: Array.isArray(queryKey) ? queryKey : [queryKey] });
          }}
          departments={departments}
        />
      )}
    </div>
  );
}

function ChecklistEditor({
  selected,
  withDifficulty,
  withDepartmentCategory,
  testIdPrefix,
  apiPath,
  editPermission,
  readOnly,
  guard,
  onPatch,
  onDeleted,
}) {
  const [tax, setTax] = useState({
    industryId: '',
    departmentId: selected.department_id || '',
    roleId: selected.role_id || '',
  });
  const [topic, setTopic] = useState(selected.topic || '');
  const onTaxChange = useCallback((next) => {
    setTax((prev) => {
      if (withDepartmentCategory && next.departmentId !== prev.departmentId) {
        setTopic('');
      }
      return next;
    });
  }, [withDepartmentCategory]);

  const scopeDirty = useMemo(
    () => tax.roleId !== (selected.role_id || '')
      || tax.departmentId !== (selected.department_id || '')
      || topic !== (selected.topic || ''),
    [tax.roleId, tax.departmentId, topic, selected.role_id, selected.department_id, selected.topic],
  );

  const { data: workCategories = [] } = useQuery({
    queryKey: ['v1-work-categories', tax.departmentId],
    queryFn: () => v1Get(`/questions/work-categories?department_id=${tax.departmentId}`),
    enabled: withDepartmentCategory && !!tax.departmentId,
  });

  const saveScope = guard(async () => {
    if (!scopeDirty) return;
    if (!tax.roleId) return toast.error('Industry → Department → Role is required');
    if (!tax.departmentId) return toast.error('Department is required');
    if (withDepartmentCategory && !topic) return toast.error('Work category is required');
    const patch = {
      role_id: tax.roleId,
      department_id: tax.departmentId,
      topic: withDepartmentCategory ? topic : (topic || null),
    };
    if (selected.status === 'active' && !tax.roleId) {
      patch.status = 'draft';
    }
    await onPatch(patch);
    toast.success('Saved');
  });

  const setStatusSafe = (v) => {
    if (v === selected.status) return;
    if (v === 'active' && !tax.roleId) {
      toast.error('Role is required to activate — keeping draft');
      return;
    }
    onPatch({ status: v });
  };

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-editor`}>
      {readOnly && (
        <QuestionBankReadOnlyBanner testId={`${testIdPrefix}-read-only`} />
      )}
      <IndustryDeptRoleCascade
        value={tax}
        onChange={onTaxChange}
        required
        disabled={readOnly}
        testIdPrefix={`${testIdPrefix}-editor`}
      />
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Question text
        </p>
        <Textarea
          defaultValue={selected.text}
          onBlur={(e) => e.target.value.trim() !== selected.text && onPatch({ text: e.target.value.trim() })}
          rows={3}
          disabled={readOnly}
          className="mt-1 text-sm"
          data-testid={`${testIdPrefix}-text`}
        />
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Model answer <span className="font-normal normal-case text-muted-foreground/70">— what a strong response looks like</span>
        </p>
        <Textarea
          defaultValue={selected.model_answer || ''}
          onBlur={(e) => e.target.value !== (selected.model_answer || '') && onPatch({ model_answer: e.target.value || null })}
          rows={4}
          disabled={readOnly}
          placeholder="A clear, structured answer covers…"
          className="mt-1 text-sm"
          data-testid={`${testIdPrefix}-model-answer`}
        />
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">
          Red flags <span className="font-normal normal-case text-muted-foreground/70">— things to watch out for</span>
        </p>
        <Textarea
          defaultValue={selected.red_flags || ''}
          onBlur={(e) => e.target.value !== (selected.red_flags || '') && onPatch({ red_flags: e.target.value || null })}
          rows={3}
          disabled={readOnly}
          placeholder="No mention of trade-offs; defensive when pressed…"
          className="mt-1 text-sm border-rose-200 focus-visible:ring-rose-300"
          data-testid={`${testIdPrefix}-red-flags`}
        />
      </div>
      {withDepartmentCategory ? (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Category</p>
          <Select value={topic} onValueChange={setTopic} disabled={readOnly || !tax.departmentId}>
            <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-topic`}>
              <SelectValue placeholder={tax.departmentId ? 'Select category' : 'Pick department first'} />
            </SelectTrigger>
            <SelectContent>
              {workCategories.map((c) => (
                <SelectItem key={c.id || c.label} value={c.label}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {tax.departmentId && workCategories.length === 0 && (
            <p className="mt-1 text-[11px] text-amber-800">
              No categories for this department.{' '}
              <Link to="/portal/settings/roles" className="font-medium underline underline-offset-2">
                Add categories in Roles &amp; Departments
              </Link>
            </p>
          )}
        </div>
      ) : (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Topic</p>
          <Input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onBlur={() => {
              const next = topic || null;
              if (next !== (selected.topic || null)) {
                // Scope save button also covers topic; blur keeps offer free-text in sync when not dirty via role.
                if (!scopeDirty || (tax.roleId === (selected.role_id || '') && tax.departmentId === (selected.department_id || ''))) {
                  onPatch({ topic: next });
                }
              }
            }}
            disabled={readOnly}
            className="mt-1 text-sm"
            placeholder="e.g. Architecture"
            data-testid={`${testIdPrefix}-topic`}
          />
        </div>
      )}
      <div className={`grid grid-cols-1 gap-3 ${withDifficulty ? 'md:grid-cols-2' : ''}`}>
        {withDifficulty && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Difficulty</p>
            <Select
              value={selected.difficulty}
              onValueChange={(v) => v !== selected.difficulty && onPatch({ difficulty: v })}
              disabled={readOnly}
            >
              <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-difficulty`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIFFICULTIES.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</p>
          <Select
            value={selected.status}
            onValueChange={setStatusSafe}
            disabled={readOnly}
          >
            <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-status`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3">
        <QuestionEditorDeleteButton
          apiPath={apiPath}
          questionId={selected.id}
          questionPreview={selected.text}
          editPermission={editPermission}
          onDeleted={onDeleted}
          testId={`${testIdPrefix}-delete`}
        />
        <Button
          size="sm"
          onClick={saveScope}
          disabled={readOnly || !scopeDirty}
          className="gap-1.5"
          data-testid={`${testIdPrefix}-save-scope`}
        >
          <Save className="h-3 w-3" /> Save taxonomy{withDepartmentCategory ? ' & category' : ''}
        </Button>
        {!readOnly && (
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            Text fields auto-save on blur
          </span>
        )}
      </div>
    </div>
  );
}
